import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, X, Sparkles } from "lucide-react";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About", path: "/about" },
  { name: "Service Areas", path: "/service-areas" },
  { name: "Contact", path: "/contact" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-secondary">
              CartNexa
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium text-sm transition-colors hover:text-primary ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-slate-600"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+18717871311"
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-primary transition-colors"
              data-testid="phone-link-header"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">+1-871-7871311</span>
            </a>
            <Link to="/#quote-form">
              <Button
                className="bg-accent hover:bg-accent/90 text-white font-bold rounded-full px-6 btn-accent-hover"
                data-testid="get-quote-btn-header"
              >
                Get Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <div className="flex flex-col h-full pt-8">
                <nav className="flex flex-col gap-4" data-testid="mobile-nav">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`font-medium text-lg py-2 border-b border-slate-100 transition-colors ${
                        location.pathname === link.path
                          ? "text-primary"
                          : "text-slate-700"
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pb-8 space-y-4">
                  <a
                    href="tel:+18717871311"
                    className="flex items-center gap-3 text-lg font-medium text-primary"
                    data-testid="phone-link-mobile"
                  >
                    <Phone className="w-5 h-5" />
                    +1-871-7871311
                  </a>
                  <Link to="/#quote-form" className="block">
                    <Button className="w-full bg-accent hover:bg-accent/90 text-white font-bold rounded-full py-6">
                      Get Free Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
